import { appUrl, firestoreTaskPath, taskPath } from "@shared/appUrls";
import type { CocoExportSource } from "@/services/cocoExportService";
import type { Experiment } from "@/types/experiment";
import type { Organization } from "@/types/organization";
import type { Trial } from "@/types/trial";
import { EXPORT_PATH_BUDGET, type ExportFileNames, type MediaDownloadOptions } from "./exportPaths";
import { encodeFilenameSegment } from "./mediaUtils";

/**
 * Contents of export_info.json: which Firestore task an export came from, with full names.
 *
 * File names can't carry this reliably (task names are shortened to keep paths under the Windows
 * limit, aren't unique, and lose characters), so scripts should match exports to tasks with
 * `firebaseProjectId` + `taskPath`. `appUrl` is a convenience link, not provenance.
 */
export interface ExportInfo {
  schemaVersion: 1;
  exportedAt: string;
  /** Source database: demo-test (emulators), gleanag-staging, or glean-ag */
  firebaseProjectId: string | null;
  zipFilename: string;
  organization: { id: string | null; name: string | null };
  trial: { id: string | null; name: string | null };
  experiment: { id: string | null; name: string | null };
  task: { id: string | null; name: string };
  /** Same value as COCO's info.gleanag_task_path */
  taskPath: string | null;
  appUrl: string | null;
  fileNames: {
    /** The task name as it appears in the ZIP and CSV names */
    taskLabel: string;
    taskLabelShortened: boolean;
    timestamp: string;
    /** Plot IDs written differently in media file names because Windows forbids some of their characters */
    plotIdsChangedInFileNames: Array<{ plotId: string; fileNamePlotId: string }>;
  };
  pathCheck: { limit: number; longestPathLength: number; withinLimit: boolean };
  mediaOptions: MediaDownloadOptions;
}

export interface ExportInfoInput {
  exportedAt: Date;
  fileNames: ExportFileNames;
  firebaseProjectId: string | null;
  appOrigin: string | null;
  /** The task identity, already checked against the route and loaded documents; null when unverified */
  source: CocoExportSource | null;
  /** Loaded documents, used only for names that belong to `source` */
  experiment: Pick<Experiment, "id" | "name">;
  trial?: Pick<Trial, "id" | "name"> | null;
  organization?: Pick<Organization, "id" | "name"> | null;
  taskName: string;
  /** Plot doc IDs used in this export's media file names */
  plotIds: readonly string[];
  mediaOptions: MediaDownloadOptions;
}

/**
 * Build export_info.json. Identity comes only from the verified source; a name is recorded only
 * when its document is the one the source names, and anything unverified is null.
 */
export function buildExportInfo(input: ExportInfoInput): ExportInfo {
  const { source, experiment, trial, organization } = input;
  const nameIfSame = (doc: { id?: string; name?: string } | null | undefined, id: string | undefined) =>
    doc && id && doc.id === id ? (doc.name ?? null) : null;

  const plotIdsChangedInFileNames = [...new Set(input.plotIds)]
    .sort()
    .map((plotId) => ({ plotId, fileNamePlotId: encodeFilenameSegment(plotId) }))
    .filter(({ plotId, fileNamePlotId }) => fileNamePlotId !== plotId);

  return {
    schemaVersion: 1,
    exportedAt: input.exportedAt.toISOString(),
    firebaseProjectId: input.firebaseProjectId,
    zipFilename: input.fileNames.zipFilename,
    organization: { id: source?.organizationId ?? null, name: nameIfSame(organization, source?.organizationId) },
    trial: { id: source?.trialId ?? null, name: nameIfSame(trial, source?.trialId) },
    experiment: { id: source?.experimentId ?? null, name: nameIfSame(experiment, source?.experimentId) },
    task: { id: source?.taskId ?? null, name: input.taskName },
    taskPath: source
      ? firestoreTaskPath(source.organizationId, source.trialId, source.experimentId, source.taskId)
      : null,
    appUrl:
      source && input.appOrigin
        ? appUrl(input.appOrigin, taskPath(source.organizationId, source.trialId, source.experimentId, source.taskId))
        : null,
    fileNames: {
      taskLabel: input.fileNames.taskLabel,
      taskLabelShortened: input.fileNames.taskLabelShortened,
      timestamp: input.fileNames.timestamp,
      plotIdsChangedInFileNames,
    },
    pathCheck: {
      limit: EXPORT_PATH_BUDGET,
      longestPathLength: input.fileNames.longestPath.length,
      withinLimit: input.fileNames.withinBudget,
    },
    mediaOptions: input.mediaOptions,
  };
}
